import {
  KeycloakAccount,
  KeycloakCredentialContainer,
  KeycloakDevice,
} from "core/types"
import { env } from "../env"
import {
  ClinicListType,
  KCSessionDevicesResponseType,
  UserCredentialsType,
} from "../interfaces/LoginSecurity"
import {
  USER_ACCOUNT_URL,
  USER_CREDENTIALS_URL,
  USER_DEVICES_URL,
  USER_INFO_URL,
  USER_LINKED_CLINICS_URL,
  USER_SESSIONS_URL,
} from "./constants"

const jsonHeaders = {
  "Content-Type": "application/json",
}

const getJson = <T>(url: string): Promise<T> =>
  fetch(url, { method: "GET", headers: jsonHeaders }).then((response) => {
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`)
    }
    return response.json() as Promise<T>
  })

const requestDelete = (url: string): Promise<Response> =>
  fetch(url, { method: "DELETE", headers: jsonHeaders }).then((response) => {
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`)
    }
    return response
  })

export const GetAccountInitialData = async (): Promise<{
  account: PromiseSettledResult<KeycloakAccount>
  credentials: PromiseSettledResult<KeycloakCredentialContainer[]>
  devices: PromiseSettledResult<KeycloakDevice[]>
}> => {
  const [account, credentials, devices] = await Promise.allSettled([
    getJson<KeycloakAccount>(USER_ACCOUNT_URL),
    getJson<KeycloakCredentialContainer[]>(USER_CREDENTIALS_URL),
    getJson<KeycloakDevice[]>(USER_DEVICES_URL),
  ])

  return { account, credentials, devices }
}

export const GetDevices = (): Promise<KeycloakDevice[]> =>
  getJson<KeycloakDevice[]>(USER_DEVICES_URL)

export const DeleteSession = (sessionId: string): Promise<Response> =>
  requestDelete(`${USER_SESSIONS_URL}/${sessionId}`)

/**
 * `current` defaults to false server-side, which Keycloak reads as "sign out
 * every session except mine". Pass it explicitly so this session is gone even
 * if the caller's subsequent logout redirect never completes.
 */
export const DeleteAllSessions = (): Promise<Response> =>
  requestDelete(`${USER_SESSIONS_URL}?current=true`)

/**
 * The account API answers a revoked session with a 401 that has no CORS
 * headers, so fetch reports it as a network error, indistinguishable from an
 * outage. Keycloak's userinfo endpoint does send CORS headers on its 401s, so
 * the session's state is readable here. Failing to reach it is an outage, not a
 * revocation.
 */
export const IsSessionRevoked = async (): Promise<boolean> => {
  try {
    const response = await fetch(USER_INFO_URL)
    return response.status === 401
  } catch (error) {
    return false
  }
}

export const GetInitialDataCalls = async (): Promise<{
  account: PromiseSettledResult<UserCredentialsType>
  devices: PromiseSettledResult<KCSessionDevicesResponseType[]>
  clinics: PromiseSettledResult<{ data: ClinicListType }>
}> => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  }

  const urls: string[] = [
    USER_ACCOUNT_URL,
    USER_DEVICES_URL,
    USER_LINKED_CLINICS_URL,
  ]

  const accountDataRequests = urls.map((url) => {
    return fetch(url, options)
      .then((response) => {
        if (response.ok) {
          return response.json()
        }
        throw new Error(`${response.status} ${response.statusText}`)
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  })

  const accountDataResults = await Promise.allSettled(accountDataRequests)
  const accountDataCollectedResults = await {
    account: accountDataResults[0],
    devices: accountDataResults[1],
    clinics: accountDataResults[2],
  }

  return accountDataCollectedResults
}

export const DeleteSessionByDeviceId = (deviceId: string) => {
  const options = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  }
  return fetch(
    `${env.REACT_APP_KEYCLOAK_BASE_URL}/account/sessions/${deviceId}`,
    options
  )
    .then((res) => {
      console.log(res)
      return res
    })
    .catch((err) => console.log(err))
}


export const LinkedClinics = () => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  }
  return fetch(USER_LINKED_CLINICS_URL, options)
    .then((response) => {
      if (response.ok) {
        return response.json()
      }
      throw new Error(`${response.status} ${response.statusText}`)
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

export const QueryClinics = (query: string) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  }
  return fetch(`${env.REACT_APP_ID_API_BASE_URL}/clinics?q=${query}`, options)
    .then((res) => {
      if (res.ok) {
        return res.json()
      }
      throw new Error(`${res.status} ${res.statusText}`)
    })
    .catch((err) => console.log(err))
}
